import { useForm } from '@inertiajs/react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Textarea } from '@/shared/ui/textarea'
import { t } from '@/shared/lib/i18n'
import { complianceBulkReviewPath } from '@/shared/lib/routes'

interface ReviewStatusOption {
  id: number
  label: string
}

interface Props {
  selectedIds: Set<number>
  maxCount: number
  limitReached: boolean
  reviewStatuses: ReviewStatusOption[]
  onClearSelection: () => void
}

const BulkActionBar = ({
  selectedIds,
  maxCount,
  limitReached,
  reviewStatuses,
  onClearSelection,
}: Props) => {
  const form = useForm({
    compliance_review_status_id: '',
    bulk_comments: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((formData) => ({ ...formData, compliance_ids: Array.from(selectedIds) }))
    form.post(complianceBulkReviewPath(), {
      onSuccess: () => onClearSelection(),
    })
  }

  return (
    <div className="sticky bottom-0 z-10 rounded-md border bg-background p-4 shadow-md">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-3">
        <Badge variant={limitReached ? 'destructive' : 'secondary'} className="tabular-nums">
          {selectedIds.size} / {maxCount}
        </Badge>

        {limitReached && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {t('compliance.dashboard.bulkLimitReached').replace('{max}', String(maxCount))}
          </span>
        )}

        <Select
          value={form.data.compliance_review_status_id}
          onValueChange={(value) => form.setData('compliance_review_status_id', value)}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder={t('compliance.dashboard.bulkActionLabel')} />
          </SelectTrigger>
          <SelectContent>
            {reviewStatuses.map((status) => (
              <SelectItem key={status.id} value={String(status.id)}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Textarea
          placeholder={t('compliance.dashboard.bulkComments')}
          value={form.data.bulk_comments}
          onChange={(e) => form.setData('bulk_comments', e.target.value)}
          className="h-9 min-h-0 w-64 resize-none"
          rows={1}
        />

        <Button
          type="submit"
          disabled={
            form.processing || !form.data.compliance_review_status_id || selectedIds.size === 0
          }
        >
          {t('compliance.dashboard.bulkActionSubmit')}
        </Button>
      </form>
    </div>
  )
}

export default BulkActionBar
